import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchDropdown.module.scss';

export interface SearchDropdownOption {
    value: string;
    label: string;
    subLabel?: string | null;
}

interface SearchDropdownProps {
    id: string;
    value: string | null | undefined;
    options: SearchDropdownOption[];
    onChange: (value: string | null) => void;
    onSearchChange?: (query: string) => void;
    placeholder?: string;
    disabled?: boolean;
    clearable?: boolean;
    isLoading?: boolean;
    errorMessage?: string | null;
    noResultsText?: string;
}

const SearchDropdown = ({
    id,
    value,
    options,
    onChange,
    onSearchChange,
    placeholder = 'Search...',
    disabled = false,
    clearable = true,
    isLoading = false,
    errorMessage = null,
    noResultsText = 'No options found.'
}: SearchDropdownProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selectedOption = useMemo(
        () => options.find((option) => option.value === (value ?? '')) ?? null,
        [options, value]
    );
    const selectedLabel = selectedOption?.label ?? '';

    const closeMenu = () => {
        setIsOpen(false);
        setQuery((previous) => (previous === selectedLabel ? previous : selectedLabel));
    };

    useEffect(() => {
        if (isOpen) {
            return;
        }

        setQuery((previous) => (previous === selectedLabel ? previous : selectedLabel));
    }, [isOpen, selectedLabel]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleOutsideClick = (event: MouseEvent) => {
            if (!containerRef.current) {
                return;
            }

            if (!containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setQuery((previous) => (previous === selectedLabel ? previous : selectedLabel));
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, selectedLabel]);

    const filteredOptions = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return options.slice(0, 100);
        }

        return options
            .filter((option) => option.label.toLowerCase().includes(normalizedQuery))
            .slice(0, 100);
    }, [options, query]);

    const selectOption = (option: SearchDropdownOption) => {
        onChange(option.value);
        setQuery((previous) => (previous === option.label ? previous : option.label));
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const focusRelativeOption = (current: HTMLElement, offset: number) => {
        const optionElements = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
        const currentIndex = optionElements.indexOf(current);
        optionElements[currentIndex + offset]?.focus();
    };

    return (
        <div ref={containerRef} className={styles.root}>
            <div className={styles.inputRow}>
                <input
                    ref={inputRef}
                    id={id}
                    type="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={`${id}-options`}
                    aria-expanded={isOpen}
                    className={styles.input}
                    value={query}
                    onChange={(event) => {
                        const nextQuery = event.target.value;
                        setQuery((previous) => (previous === nextQuery ? previous : nextQuery));
                        if (!isOpen) {
                            setIsOpen(true);
                        }
                        onSearchChange?.(nextQuery);
                    }}
                    onFocus={() => {
                        if (!isOpen) {
                            setIsOpen(true);
                        }
                        onSearchChange?.(query);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            closeMenu();
                        } else if (event.key === 'ArrowDown' && filteredOptions.length > 0) {
                            event.preventDefault();
                            if (!isOpen) setIsOpen(true);
                            window.requestAnimationFrame(() => {
                                menuRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
                            });
                        }
                    }}
                    placeholder={placeholder}
                    autoComplete="off"
                    disabled={disabled}
                />
                {clearable ? (
                    <button
                        type="button"
                        className={styles.clearButton}
                        disabled={disabled || (!value && query.trim().length === 0)}
                        onClick={() => {
                            setQuery((previous) => (previous === '' ? previous : ''));
                            if (isOpen) {
                                setIsOpen(false);
                            }
                            onChange(null);
                            onSearchChange?.('');
                        }}
                    >
                        Clear
                    </button>
                ) : null}
            </div>
            {isOpen ? (
                <div id={`${id}-options`} ref={menuRef} role="listbox" className={styles.menu}>
                    {isLoading ? <div className={styles.state}>Loading...</div> : null}
                    {!isLoading && errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}
                    {!isLoading && !errorMessage && filteredOptions.length === 0 ? (
                        <div className={styles.state}>{noResultsText}</div>
                    ) : null}
                    {!isLoading && !errorMessage && filteredOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={option.value === value}
                            className={`${styles.menuItem} ${option.value === value ? styles.active : ''}`}
                            onClick={() => selectOption(option)}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    closeMenu();
                                    inputRef.current?.focus();
                                } else if (event.key === 'ArrowDown') {
                                    event.preventDefault();
                                    focusRelativeOption(event.currentTarget, 1);
                                } else if (event.key === 'ArrowUp') {
                                    event.preventDefault();
                                    focusRelativeOption(event.currentTarget, -1);
                                }
                            }}
                        >
                            <span>{option.label}</span>
                            {option.subLabel ? <span className={styles.subText}>{option.subLabel}</span> : null}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default SearchDropdown;
